<template>
  <div>
    <div
      @click="handleClick"
      :class="[
        'sidebar-item',
        `level-${level}`,
        isActive && 'active',
        hasChildren && 'has-children'
      ]"
      :style="{ paddingLeft: (level === 0 ? 40 : level * 20 + 40) + 'px' }"
      :title="item.label || item.text"
    >
      <div class="sidebar-text-group">
        <span class="sidebar-text">{{ item.label || item.text }}</span>
        <span v-if="hasChildren" class="sidebar-arrow">
          <i :class="expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" style="color:#999;font-size:14px;"></i>
        </span>
      </div>
      <img :src="isActive ? require('@/assets/list/active.png') : require('@/assets/list/actived.png')"
           alt="" class="sidebar-leaf-icon">
    </div>
    <el-divider v-if="level === 0 && !showChildren" class="sidebar-divider"></el-divider>
    <div v-if="showChildren">
      <sidebar-menu-item
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        :level="level + 1"
        :active-id="activeId"
        @select="handleChildSelect"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'SidebarMenuItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    activeId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      expanded: false
    }
  },
  computed: {
    hasChildren() {
      return this.item.children && this.item.children.length > 0
    },
    isActive() {
      return String(this.item.id) === String(this.activeId)
    },
    showChildren() {
      return this.hasChildren && this.expanded
    }
  },
  methods: {
    handleClick() {
      if (this.hasChildren) {
        this.expanded = !this.expanded
      }
      this.$emit('select', this.item)
    },
    handleChildSelect(item) {
      this.$emit('select', item)
    },
    deepFind(children, id) {
      for (const c of children) {
        if (String(c.id) === String(id)) return true
        if (c.children && this.deepFind(c.children, id)) return true
      }
      return false
    }
  },
  watch: {
    activeId: {
      immediate: true,
      handler(val) {
        if (!this.hasChildren || !val) return
        // 展开条件：active 就是本项，或者是本项的后代
        const match = String(this.item.id) === String(val) ||
          (this.item.children || []).some(c =>
            String(c.id) === String(val) ||
            (c.children && this.deepFind(c.children, val))
          )
        if (match) {
          this.expanded = true
        }
      }
    }
  }
}
</script>

<style scoped>
.sidebar-item {
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 10px;
  padding-top: 3px;
  padding-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease;
  margin: 2px 0;
}

.sidebar-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-text-group {
  display: flex;
  align-items: center;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.sidebar-arrow {
  margin-right: 4px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}

.sidebar-leaf-icon {
  width: 13px;
  height: 12px;
  margin-right: 40px;
  flex-shrink: 0;
}

.sidebar-divider {
  width: calc(100% - 80px);
  margin: 8px auto !important;
}

/* Level 0: First level — keep original style with arrows/images */
.level-0 {
  font-size: 20px;
  font-weight: 400;
  color: #333333;
  padding-left: 40px;
}
.level-0.active {
  color: #176DAA;
  font-weight: 700;
  border-left: 4px solid #176DAA;
}
.level-0:hover {
  color: #176DAA;
  font-weight: 700;
  border-left: 4px solid #176DAA;
}

/* Level 1+ with children — "redesigned" multi-level parent items */
.level-1.has-children {
  font-size: 18px;
  font-weight: 600;
  color: #176DAA;
  background: #ebf4fb;
  border-radius: 6px;
  margin: 4px 8px;
  width: auto;
  padding-top: 6px;
  padding-bottom: 6px;
}

/* Level 1+ without children (leaf items at deeper levels) */
.level-1:not(.has-children) {
  font-size: 18px;
  font-weight: 400;
  color: #555555;
}
.level-1:not(.has-children).active {
  color: #176DAA;
  font-weight: 700;
  border-left: 4px solid #176DAA;
  background: #f0f9ff;
}
.level-1:not(.has-children):hover {
  color: #176DAA;
}

/* Level 2+ (third level and deeper) */
.level-2.has-children {
  font-size: 16px;
  font-weight: 600;
  color: #555;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 2px 12px;
  width: auto;
  padding-top: 5px;
  padding-bottom: 5px;
}
.level-2:not(.has-children) {
  font-size: 16px;
  font-weight: 400;
  color: #666;
}
.level-2:not(.has-children).active {
  color: #176DAA;
  font-weight: 700;
  border-left: 4px solid #176DAA;
  background: #f0f9ff;
}
.level-2:not(.has-children):hover {
  color: #176DAA;
}

/* Level 3+ */
.level-3.has-children {
  font-size: 15px;
  font-weight: 600;
  color: #555;
  background: #f5f5f5;
  border-radius: 4px;
  margin: 2px 16px;
  width: auto;
  padding-top: 4px;
  padding-bottom: 4px;
}
.level-3:not(.has-children) {
  font-size: 15px;
  font-weight: 400;
  color: #666;
}
.level-3:not(.has-children).active {
  color: #176DAA;
  font-weight: 700;
  border-left: 3px solid #176DAA;
  background: #f0f9ff;
}
.level-3:not(.has-children):hover {
  color: #176DAA;
}
</style>
